import { useQuery } from '@tanstack/react-query'
import { queryDeskCandidate, queryDeskRawItem, queryDeskSiblings } from 'supabase/desk'

/**
 * Everything the candidate screen reads about one candidate: the application
 * row, its raw item (the source post) and the sibling candidates of the same
 * raw item. The raw item and siblings wait for the candidate, since their key
 * is its raw_item_id. Every key starts with 'desk' so a decision refreshes all
 * three.
 *
 * `id` may be null/undefined while a route param is being read; nothing is
 * fetched until it is a number.
 */
export const useQueryDeskCandidate = (id: number | null | undefined) => {
  const hasId = typeof id === 'number' && Number.isFinite(id)

  const candidateQuery = useQuery({
    queryKey: ['desk', 'candidate', id],
    queryFn: () => queryDeskCandidate(id as number),
    enabled: hasId
  })

  const rawItemId = candidateQuery.data?.raw_item_id ?? null
  const hasRawItem = rawItemId !== null

  const rawItemQuery = useQuery({
    queryKey: ['desk', 'raw', rawItemId],
    queryFn: () => queryDeskRawItem(rawItemId as number),
    enabled: hasRawItem
  })

  const siblingsQuery = useQuery({
    queryKey: ['desk', 'siblings', rawItemId],
    queryFn: () => queryDeskSiblings(rawItemId as number),
    enabled: hasRawItem
  })

  const queries = [candidateQuery, rawItemQuery, siblingsQuery]
  const failed = queries.find((query) => query.isError)

  return {
    // Loading until the candidate and, when it has one, its raw item and
    // siblings are in. A disabled query reports isLoading false in v5.
    isLoading: queries.some((query) => query.isLoading),
    isError: failed !== undefined,
    error: failed?.error ?? null,
    candidate: candidateQuery.data,
    rawItem: rawItemQuery.data,
    siblings: siblingsQuery.data
  }
}
